/**
 * @documental
 * @export Overlay
 * @import
 * import { Overlay } from "capitalrx-components-2.0/lib/general";
 */

import { FC, useContext, useEffect, useRef, useState, useCallback, PropsWithChildren } from "react";
import { createPortal } from "react-dom";
import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Bounds, BoundsObserver } from "../../utilities/BoundsObserver";
import { useId } from "../../hooks/useId";
import { OverlayContext } from "./OverlayContext";
import { BoundsAdjustmentCallback } from "./BoundsAdjustmentCallback";
import { ElementBounds } from "./ElementBounds";
import { getAdjustedOverlayBounds, updateOverlayBounds } from "./helpers";
import { createComponentStyleHook } from "../../tokens";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";

export interface OverlayHTMLElement extends HTMLDivElement {
  /**
   * The overlay parent redirect ID.
   */
  $overlayParentRedirect?: string | null;
}

/**
 * The `Overlay` component properties.
 */

export interface OverlayProps extends BaseProps {
  /**
   * Indicates how the overlay should be able to be adjusted to maintain its visibility within the viewport.
   * If not set, defaults to `"none"`.
   */
  readonly adjust?: "horizontal" | "vertical" | "both" | "none" | BoundsAdjustmentCallback;
}

const useStyles = createComponentStyleHook<OverlayProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "overlay",
});

/**
 * A component which moves its children out to a different visual layer on the page while retaining their position within their
 * original document flow. Useful for content that needs to not be subject to HTML overflow clipping, such as tooltips, dropdown
 * lists, menus, etc.
 */
export const Overlay: FC<PropsWithChildren<OverlayProps>> = (props) => {
  const { children, adjust = "none" } = props;
  const { mode, styles, StyleModeBoundary } = useStyles(props);

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Overlay, "overlay"],
    testIdPrefix: "overlay",
  });

  const floatingBaseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.FloatingOverlay, "floating-overlay"],
    testIdPrefix: "floating-overlay",
  });

  const id = useId();
  const { element } = useContext(OverlayContext);
  const containerElementRef = useRef<HTMLElement | null>(null);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const overlayElementRef = useRef<OverlayHTMLElement | null>(null);
  const [ready, setReady] = useState(false);

  const containerBoundsRef = useRef<Bounds | null>(null);
  const elementBoundsRef = useRef<ElementBounds | null>(null);
  const previousOverlayBoundsRef = useRef<Bounds | null>(null);

  const adjustRef = useRef<OverlayProps["adjust"]>(adjust);
  useEffect(() => {
    adjustRef.current = adjust;
  }, [adjust]);

  const doOverlayPositionUpdate = useCallback(() => {
    const { current: overlayElement } = overlayElementRef;

    if (!elementBoundsRef.current || !containerBoundsRef.current || !overlayElement) {
      return;
    }

    let adjustmentMode: "horizontal" | "vertical" | "both" | "none" = "none";
    if (adjustRef.current && typeof adjustRef.current !== "function") adjustmentMode = adjustRef.current;
    const baseBounds = getAdjustedOverlayBounds(elementBoundsRef.current.bounds, containerBoundsRef.current, adjustmentMode);

    let overlayBounds = baseBounds;

    if (typeof adjustRef.current === "function") {
      const adjustment = adjustRef.current(elementBoundsRef.current, containerBoundsRef.current);

      if (adjustment.left === "auto") {
        const autoBounds = getAdjustedOverlayBounds(elementBoundsRef.current.bounds, containerBoundsRef.current, "horizontal");
        overlayBounds = {
          ...overlayBounds,
          left: autoBounds.left,
        };
      } else if (adjustment.left !== undefined) {
        overlayBounds = {
          ...overlayBounds,
          left: adjustment.left,
        };
      }

      if (adjustment.top === "auto") {
        const autoBounds = getAdjustedOverlayBounds(elementBoundsRef.current.bounds, containerBoundsRef.current, "vertical");
        overlayBounds = {
          ...overlayBounds,
          top: autoBounds.top,
        };
      } else if (adjustment.top !== undefined) {
        overlayBounds = {
          ...overlayBounds,
          top: adjustment.top,
        };
      }

      // Apply width adjustment if specified
      if (adjustment.width !== undefined) {
        overlayBounds = {
          ...overlayBounds,
          width: adjustment.width,
        };
      }

      // Apply height adjustment if specified
      if (adjustment.height !== undefined) {
        overlayBounds = {
          ...overlayBounds,
          height: adjustment.height,
        };
      }
    }

    // overlayBounds is currently positioned absolutely, we need to reposition it relative to the container's bounds.
    overlayBounds = {
      ...overlayBounds,
      top: overlayBounds.top - containerBoundsRef.current.top,
      left: overlayBounds.left - containerBoundsRef.current.left,
    };

    updateOverlayBounds(overlayElement, overlayBounds, previousOverlayBoundsRef.current);

    setReady(true);
    previousOverlayBoundsRef.current = overlayBounds;
  }, []);

  const [boundsObserver] = useState<BoundsObserver>(
    () =>
      new BoundsObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === elementRef.current) {
            elementBoundsRef.current = {
              offsetTop: entry.offsetTop,
              offsetLeft: entry.offsetLeft,
              bounds: entry.bounds,
            };
          } else if (entry.target === containerElementRef.current) {
            containerBoundsRef.current = entry.bounds;
          }
        }

        doOverlayPositionUpdate();
      }),
  );

  useEffect(() => {
    containerElementRef.current = element;

    if (containerElementRef.current) {
      boundsObserver.unobserve(containerElementRef.current);
      containerBoundsRef.current = null;
    }

    if (element) {
      boundsObserver.observe(element);
    }
  }, [element]);

  useEffect(() => {
    return (): void => {
      boundsObserver.disconnect();
    };
  }, []);

  const elementRefHandler = useCallback((ref: HTMLDivElement | null) => {
    elementRef.current = ref;

    if (!ref) {
      elementBoundsRef.current = null;
      boundsObserver.disconnect();
    }

    if (ref) {
      boundsObserver.observe(ref);
    }
  }, []);

  const overlayElementRefHandler = useCallback(
    (ref: OverlayHTMLElement | null) => {
      if (overlayElementRef.current && overlayElementRef.current !== ref) {
        overlayElementRef.current.$overlayParentRedirect = null;
      }

      overlayElementRef.current = ref;

      if (ref) {
        ref.$overlayParentRedirect = id;
      }

      doOverlayPositionUpdate();
    },
    [id, doOverlayPositionUpdate],
  );

  useEffect(() => {
    if (overlayElementRef.current) overlayElementRef.current.$overlayParentRedirect = id;

    return () => {
      if (overlayElementRef.current) overlayElementRef.current.$overlayParentRedirect = null;
    };
  }, [id]);

  if (!element) return null;

  return (
    <StyleModeBoundary mode={mode}>
      <div id={id} ref={elementRefHandler} {...baseHtmlProps}>
        {children}
      </div>
      {createPortal(
        <div className={styles.OverlaySurface} ref={overlayElementRefHandler}>
          <div {...floatingBaseHtmlProps}>{ready && children}</div>
        </div>,
        element,
      )}
    </StyleModeBoundary>
  );
};
